import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuditLog } from '../hooks/useAuditLog';
import { formatDateTime } from '../lib/format';
import type { AuditEntry } from '../types/domain';

const ACTIONS = [
  { value: 'org.create', label: 'Crear org' },
  { value: 'org.disable', label: 'Deshabilitar org' },
  { value: 'org.enable', label: 'Habilitar org' },
  { value: 'member.assign', label: 'Asignar miembro' },
  { value: 'member.remove', label: 'Eliminar miembro' },
  { value: 'user.impersonate', label: 'Impersonar usuario' },
  { value: 'admin.grant', label: 'Otorgar admin' },
  { value: 'admin.revoke', label: 'Revocar admin' },
] as const;

type BadgeVariant = 'destructive' | 'default' | 'secondary' | 'outline' | 'success' | 'warning';

const ACTION_BADGE: Record<string, BadgeVariant> = {
  'org.disable': 'destructive',
  'admin.revoke': 'destructive',
  'user.impersonate': 'warning',
  'member.remove': 'warning',
  'org.create': 'success',
  'org.enable': 'success',
  'admin.grant': 'success',
  'member.assign': 'default',
};

function ActionBadge({ action }: { action: string }) {
  const variant: BadgeVariant = ACTION_BADGE[action] ?? 'secondary';
  const label = ACTIONS.find((a) => a.value === action)?.label ?? action;
  return <Badge variant={variant}>{label}</Badge>;
}

export function AuditLog() {
  const perPage = 20;
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');

  const { data, isLoading } = useAuditLog({ page, perPage, action: actionFilter || undefined });

  const entries = data ?? [];
  const totalPages = 1; // backend does not return total; pagination resets on each page load

  const columns = [
    {
      header: 'Admin',
      accessor: (e: AuditEntry) => <span className="font-mono text-xs">{e.admin_user_id}</span>,
    },
    {
      header: 'Acción',
      accessor: (e: AuditEntry) => <ActionBadge action={e.action} />,
    },
    {
      header: 'Target',
      accessor: (e: AuditEntry) => (
        <span className="font-mono text-xs">{e.target_user_id ?? e.target_org_id ?? '—'}</span>
      ),
    },
    {
      header: 'Fecha',
      accessor: (e: AuditEntry) => formatDateTime(e.created_at),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Audit Log</h1>

        <div className="w-52">
          <Select
            value={actionFilter}
            onValueChange={(v) => {
              setActionFilter(v === '_all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Todas las acciones" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas las acciones</SelectItem>
              {ACTIONS.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={entries}
        isLoading={isLoading}
        emptyMessage="Sin registros de auditoría."
        getRowKey={(e) => e.id}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft size={14} />
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
